import type { CreditsBreakdownEntry } from "../exportTrackingApi";
import type { ExportSettingsSnapshot } from "@/lib/company-filter/export/types";

// ============================================================================
// Enums & Union Types
// ============================================================================

/** Filter segments by ownership */
export type SegmentFilterBy = "my" | "org" | "all";

// ============================================================================
// Shared Types
// ============================================================================

/** Pagination metadata */
export interface PaginationMeta {
  /** Current page number */
  current_page: number;
  /** Number of items per page */
  page_size: number;
  /** Total number of items */
  total_count: number;
  /** Total number of pages */
  total_pages: number;
}

// ============================================================================
// Segment Types
// ============================================================================

/** Metadata containing filters and configuration for a segment */
export interface SegmentMetadata {
  /** JSON string containing filter criteria */
  filters?: string;
}

/** Segment permissions */
export interface SegmentPermissions {
  /** Can view the segment */
  view: boolean;
  /** Can edit/update the segment */
  write: boolean;
  /** Can delete the segment */
  delete: boolean;
}

/** Segment entity */
export interface Segment {
  /** UUID of the segment */
  id?: string;
  /** Display name */
  name?: string;
  /** Optional description */
  description?: string | null;
  /** Metadata containing filters and configuration for the segment */
  metadata?: SegmentMetadata;
  /** UUID of the organization */
  org_id?: string;
  /** UUID of the user who created this */
  created_by_id?: string;
  /** Creation timestamp (ISO datetime) */
  inserted_at?: string;
  /** Last update timestamp (ISO datetime) */
  updated_at?: string;
  /** User permissions for this segment */
  permissions?: SegmentPermissions;
}

/** Response body for a single Segment */
export interface SingleSegmentResponse {
  data?: Segment;
}

/** An array of segments matching your query params */
export interface SegmentListResponse {
  data?: Segment[];
  meta?: PaginationMeta;
}

/** POST Body for create requests for Segments */
export interface SegmentWriteBody {
  /** Display name */
  name: string;
  /** Metadata containing filters and configuration for the segment */
  metadata: SegmentMetadata;
  /** Optional description */
  description?: string | null;
}

/** PATCH Body for update requests for Segments */
export interface SegmentUpdateBody {
  /** Display name */
  name?: string;
  /** Metadata containing filters and configuration for the segment */
  metadata?: SegmentMetadata;
  /** Optional description */
  description?: string | null;
}

// ============================================================================
// Export Quote Types
// ============================================================================

/** The Count (credits estimate) result saved with a quote */
export interface ExportQuoteEstimate {
  credits_estimate: number;
  details: Record<string, CreditsBreakdownEntry>;
  /** When the estimate was computed (ISO datetime) */
  estimated_at: string;
  /** Which payload was priced: the Universe JSON or the classic XLSX one */
  payload_kind: "universe" | "classic";
}

/** Saved company filters plus the page state the quote restores */
export type ExportQuoteSearchFilters = Record<string, unknown> & {
  fuzzySearch?: string;
  scoringSourceIds?: string[] | null;
};

/** Export quote entity: search filters + export settings + last estimate */
export interface ExportQuote {
  id: string;
  name: string;
  description?: string | null;
  search_filters: ExportQuoteSearchFilters;
  export_settings: Partial<ExportSettingsSnapshot>;
  previous_estimated?: ExportQuoteEstimate | null;
  org_id?: string;
  created_by_id?: string;
  inserted_at?: string;
  updated_at?: string;
  permissions?: SegmentPermissions;
}

export interface SingleExportQuoteResponse {
  data: ExportQuote;
}

export interface ExportQuoteListResponse {
  data: ExportQuote[];
  meta: PaginationMeta;
}

/** POST Body for create requests for Export Quotes */
export interface ExportQuoteWriteBody {
  name: string;
  description?: string | null;
  search_filters: ExportQuoteSearchFilters;
  export_settings: ExportSettingsSnapshot;
  previous_estimated?: ExportQuoteEstimate | null;
}

/** PATCH Body for update requests for Export Quotes */
export type ExportQuoteUpdateBody = Partial<ExportQuoteWriteBody>;

// ============================================================================
// Request Types
// ============================================================================

/** GET /api/segments query parameters */
export interface GetSegmentsRequest {
  /** Query param encoded FLOP Filter object */
  filters?: Record<string, unknown>;
  /** Number of results per page */
  limit?: number;
  /** Number of results to skip */
  offset?: number;
  /** Field to sort by */
  order_by?: string[];
  /** Sort direction (asc/desc) */
  order_directions?: string[];
  /** Filter segments by ownership */
  filter_by?: SegmentFilterBy;
  /** Search segments by name (case and accent insensitive) */
  search?: string;
}

// ============================================================================
// Error Types
// ============================================================================

/** API Error */
export interface ApiError {
  status: number;
  error: string;
  detail?: string;
}

/** Validation Error (e.g., duplicate name) */
export interface ValidationError {
  errors: Record<string, string[]>;
}

// ============================================================================
// Type Guards
// ============================================================================

export function isSegment(value: unknown): value is Segment {
  return (
    typeof value === "object" &&
    value !== null &&
    (!("id" in value) || typeof (value as Segment).id === "string") &&
    (!("name" in value) || typeof (value as Segment).name === "string") &&
    (!("org_id" in value) || typeof (value as Segment).org_id === "string")
  );
}

export function isPaginationMeta(value: unknown): value is PaginationMeta {
  return (
    typeof value === "object" &&
    value !== null &&
    "current_page" in value &&
    typeof (value as PaginationMeta).current_page === "number" &&
    "page_size" in value &&
    typeof (value as PaginationMeta).page_size === "number" &&
    "total_count" in value &&
    typeof (value as PaginationMeta).total_count === "number" &&
    "total_pages" in value &&
    typeof (value as PaginationMeta).total_pages === "number"
  );
}

export function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === "object" &&
    value !== null &&
    "status" in value &&
    typeof (value as ApiError).status === "number" &&
    "error" in value &&
    typeof (value as ApiError).error === "string"
  );
}

export function isValidationError(value: unknown): value is ValidationError {
  return (
    typeof value === "object" &&
    value !== null &&
    "errors" in value &&
    typeof (value as ValidationError).errors === "object"
  );
}
